import { Injectable } from '@angular/core';
import {
	IListNotificationsSet,
	INotificationsSet,
} from '@modules/notification-management/interfaces/notifications.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { clonedeep } from '@utils/helpers';
import { ISlackChannelsResponse } from '@core/services/integrations/integrations.interface';

const NOTIFICATION_SETS_INITIAL_STATE: INotificationsSet = {
	org_id: '',
	name: '',
	active: true,
	subscriptions: {
		zesty_disk: {
			critical: {
				zesty_disk_resize_failures: {
					subscribed: false,
				},
				// zesty_disk_agent_stop_reporting: {
				// 	subscribed: false,
				// 	threshold: 0,
				// 	snooze_hours: 0,
				// },
				// zesty_disk_filesystem_free_space: {
				// 	subscribed: false,
				// 	threshold: 0,
				// },
			},
			info: {
				monitored_fs_utilization: {
					subscribed: false,
					threshold: 0,
					snooze_hours: 0,
				},
				zesty_disk_fs_size: {
					subscribed: false,
					threshold: 0,
					snooze_hours: 0,
				},
				zesty_disk_resize_events: {
					subscribed: false,
				},
			},
		},
		unused_resources: {
			info: {
				unused_resources_policy_update: {
					subscribed: false,
				},
				unused_resources_actions: {
					subscribed: false,
				},
			},
		},
	},
	targets: {
		slack: {
			subscribed: false,
			channels: [],
		},
		teams: {
			subscribed: false,
		},
		email: {
			subscribed: false,
			emails: [],
		},
	},
};

@Injectable({
	providedIn: 'root',
})
export class NotificationsStateService {
	private notificationSets$: BehaviorSubject<IListNotificationsSet[]> = new BehaviorSubject<IListNotificationsSet[]>(
		[],
	);
	private notificationSet$: BehaviorSubject<INotificationsSet> = new BehaviorSubject<INotificationsSet>(
		NOTIFICATION_SETS_INITIAL_STATE,
	);
	private slackChannels$: BehaviorSubject<ISlackChannelsResponse> = new BehaviorSubject<ISlackChannelsResponse>({
		message: '',
		channels: [],
	});

	private updating$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private updatingModal$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor() {}

	public resetNotificationSet(): void {
		this.notificationSet$.next(NOTIFICATION_SETS_INITIAL_STATE);
	}

	public pushNotificationSet(notificationSetResponse): void {
		const currentSets = clonedeep(this.notificationSets$.getValue());
		this.notificationSets$.next([...currentSets, notificationSetResponse]);
	}

	public setNotificationSet(notificationSet: INotificationsSet): void {
		this.notificationSet$.next(notificationSet);
	}

	public getEntity(): Observable<INotificationsSet> {
		return this.notificationSet$.asObservable();
	}

	public getSlackChannels(): Observable<ISlackChannelsResponse> {
		return this.slackChannels$.asObservable();
	}

	public setSlackChannels(channels: ISlackChannelsResponse): void {
		this.slackChannels$.next(channels);
	}

	public isUpdating$(): Observable<boolean> {
		return this.updating$.asObservable();
	}

	public isUpdatingModal$(): Observable<boolean> {
		return this.updatingModal$.asObservable();
	}

	public setUpdating(isUpdating: boolean): void {
		this.updating$.next(isUpdating);
	}

	public setUpdatingModal(isUpdating: boolean): void {
		this.updatingModal$.next(isUpdating);
	}

	public getNotificationSetsValue(): IListNotificationsSet[] {
		return this.notificationSets$.getValue();
	}

	public getNotificationSets(): Observable<IListNotificationsSet[]> {
		return this.notificationSets$.asObservable();
	}

	public setNotificationSets(notificationSets: IListNotificationsSet[]): void {
		this.notificationSets$.next(notificationSets);
	}

	public addNotificationSet(notificationsSet: IListNotificationsSet): void {
		const currentValue = this.notificationSets$.getValue();
		this.notificationSets$.next([...currentValue, notificationsSet]);
	}

	public deleteNotificationSet(name: string): void {
		const sets = clonedeep(this.notificationSets$.value);
		const filterSets = sets.filter((set) => set.name !== name);
		this.notificationSets$.next(filterSets);
	}

	public toggleActivate(name: string): void {
		const sets = clonedeep(this.notificationSets$.value);
		const mappedSets = sets.map((set) => {
			if (set.name === name) {
				return { ...set, active: !set.active };
			}
			return set;
		});
		this.notificationSets$.next(mappedSets);
	}
}
